import React, { useState, useEffect } from 'react';
import ReactToastify from '../../hooks/useReactToastify';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Input,
  FormControl,
  FormLabel,
  SimpleGrid,
  Center,
  Box,
} from '@chakra-ui/react';
import { useLazyGetUserByIdQuery } from '../../features/apis/authApi';
import { useUpdateEmployeeFinanceMutation } from '../../features/apis/salarySlipApi';
import FormSkeleton from '../../components/Skeletons/FormSkeleton';
interface UpdateSalaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  user_id: string | undefined;
}

const UpdateSalaryModal: React.FC<UpdateSalaryModalProps> = ({
  isOpen,
  onClose,
  user_id,
}) => {
  const [salaryDetails, setSalaryDetails] = useState({
    basic: '',
    hra: '',
    lta: '',
    misc: '',
    reimbursement: '',
  });
  const [bankDetails, setBankDetails] = useState({
    account_holder_name: '',
    account_number: '',
    bank_account_type: '',
    bank_branch_address: '',
    bank_ifsc: '',
    bank_name: '',
  });

  const salaryFields = ['basic', 'hra', 'lta', 'misc', 'reimbursement'];

  // Use lazy query
  const [getUserById, { data, isFetching }] = useLazyGetUserByIdQuery();
  const [updateFinance, { isLoading: isUpdating }] =
    useUpdateEmployeeFinanceMutation();

  // Fetch user data when modal opens
  useEffect(() => {
    if (isOpen && user_id) {
      getUserById({ _id: user_id });
    }
  }, [isOpen, user_id, getUserById]);

  // Populate form when data arrives
  useEffect(() => {
    if (data?.user) {
      if (data.user.salary_details) {
        setSalaryDetails({ ...data.user.salary_details });
      }
      if (data.user.bank_details) {
        setBankDetails({ ...data.user.bank_details });
      }
    }
  }, [data]);

  const handleSalaryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSalaryDetails({ ...salaryDetails, [e.target.name]: e.target.value });
  };

  const handleBankChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBankDetails({ ...bankDetails, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user_id) return;

    const parsedSalary = Object.fromEntries(
      Object.entries(salaryDetails).map(([key, value]) => [
        key,
        value === '' ? 0 : Number(value),
      ])
    );

    try {
      await updateFinance({
        _id: user_id,
        salary_details: parsedSalary,
        bank_details: bankDetails,
      }).unwrap();

      ReactToastify({
        success: true,
        message: 'Salary details updated successfully',
      });
      onClose();
    } catch (err: any) {
      ReactToastify({
        success: false,
        message: err?.data?.message || 'Update failed',
      });
    }
  };

  const labels: Record<string, string> = {
    basic: 'Basic Salary',
    hra: 'House Rent Allowance',
    lta: 'Leave Travel Allowance',
    misc: 'Miscellaneous Allowance',
    reimbursement: 'Reimbursement',
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="2xl">
      <ModalOverlay bg="blackAlpha.200" backdropFilter="blur(4px)" />{' '}
      <ModalContent mx={4}>
        <ModalHeader textAlign="left" mt={8} fontSize="sm" pb={2}>
          <Box as="span" mr={3}>
            {data?.user?.name?.split(' ')[0]}&apos;s Salary & Bank Details
          </Box>
          <hr className="mt-2 mb-1 border-t-[0.5px] border-[#D4D4D4]" />{' '}
        </ModalHeader>
        <ModalCloseButton />
        {isFetching ? (
          <Center h="60vh">
            <FormSkeleton
              sections={[
                { fields: 6, spacing: 4 },
                { fields: 5, spacing: 3 },
              ]}
              showButton
            />
          </Center>
        ) : (
          <form onSubmit={handleSubmit}>
            <ModalBody>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel
                    htmlFor="account_holder_name"
                    fontSize="12px"
                    fontWeight="bold"
                  >
                    Account Holder Name
                  </FormLabel>
                  <Input
                    id="account_holder_name"
                    name="account_holder_name"
                    value={bankDetails.account_holder_name}
                    fontSize="11px"
                    onChange={handleBankChange}
                    border="0.5px solid"
                    borderColor="#D4D4D4"
                    _focus={{ borderColor: '#D4D4D4', boxShadow: 'none' }}
                    _hover={{ borderColor: '#D4D4D4' }}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel
                    htmlFor="account_number"
                    fontSize="12px"
                    fontWeight="bold"
                  >
                    Account Number
                  </FormLabel>
                  <Input
                    id="account_number"
                    name="account_number"
                    value={bankDetails.account_number}
                    fontSize="11px"
                    onChange={handleBankChange}
                    border="0.5px solid"
                    borderColor="#D4D4D4"
                    _focus={{ borderColor: '#D4D4D4', boxShadow: 'none' }}
                    _hover={{ borderColor: '#D4D4D4' }}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel
                    htmlFor="bank_account_type"
                    fontSize="12px"
                    fontWeight="bold"
                  >
                    Account Type
                  </FormLabel>
                  <Input
                    id="bank_account_type"
                    name="bank_account_type"
                    value={bankDetails.bank_account_type}
                    fontSize="11px"
                    onChange={handleBankChange}
                    border="0.5px solid"
                    borderColor="#D4D4D4"
                    _focus={{ borderColor: '#D4D4D4', boxShadow: 'none' }}
                    _hover={{ borderColor: '#D4D4D4' }}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel
                    htmlFor="bank_branch_address"
                    fontSize="12px"
                    fontWeight="bold"
                  >
                    Branch Address
                  </FormLabel>
                  <Input
                    id="bank_branch_address"
                    name="bank_branch_address"
                    value={bankDetails.bank_branch_address}
                    fontSize="11px"
                    onChange={handleBankChange}
                    border="0.5px solid"
                    borderColor="#D4D4D4"
                    _focus={{ borderColor: '#D4D4D4', boxShadow: 'none' }}
                    _hover={{ borderColor: '#D4D4D4' }}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel
                    htmlFor="bank_ifsc"
                    fontSize="12px"
                    fontWeight="bold"
                  >
                    IFSC Code
                  </FormLabel>
                  <Input
                    id="bank_ifsc"
                    name="bank_ifsc"
                    value={bankDetails.bank_ifsc}
                    fontSize="11px"
                    onChange={handleBankChange}
                    border="0.5px solid"
                    borderColor="#D4D4D4"
                    _focus={{ borderColor: '#D4D4D4', boxShadow: 'none' }}
                    _hover={{ borderColor: '#D4D4D4' }}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel
                    htmlFor="bank_name"
                    fontSize="12px"
                    fontWeight="bold"
                  >
                    Bank Name
                  </FormLabel>
                  <Input
                    id="bank_name"
                    name="bank_name"
                    value={bankDetails.bank_name}
                    fontSize="11px"
                    onChange={handleBankChange}
                    border="0.5px solid"
                    borderColor="#D4D4D4"
                    _focus={{ borderColor: '#D4D4D4', boxShadow: 'none' }}
                    _hover={{ borderColor: '#D4D4D4' }}
                  />
                </FormControl>
              </SimpleGrid>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mt={6}>
                {salaryFields.map((field) => (
                  <FormControl key={field}>
                    <FormLabel
                      htmlFor={field}
                      fontSize="12px"
                      fontWeight="bold"
                    >
                      {labels[field]}
                    </FormLabel>
                    <Input
                      id={field}
                      name={field}
                      type="number"
                      value={(salaryDetails as any)[field]}
                      onChange={handleSalaryChange}
                      fontSize="11px"
                      border="0.5px solid"
                      borderColor="#D4D4D4"
                      _focus={{ borderColor: '#D4D4D4', boxShadow: 'none' }}
                      _hover={{ borderColor: '#D4D4D4' }}
                    />
                  </FormControl>
                ))}
              </SimpleGrid>
            </ModalBody>

            <ModalFooter mb={4}>
              <Button bg="gray.100" mr={3} onClick={onClose}>
                Cancel
              </Button>
              <Button
                bgGradient="linear(to-r, #6535EB, #9F3198)"
                _hover={{
                  bgGradient: 'linear(to-r, #7F4FF0, #B85AAE)',
                }}
                color="white"
                type="submit"
                isLoading={isUpdating}
                isDisabled={isUpdating}
              >
                {isUpdating ? 'Submitting...' : 'Submit'}
              </Button>
            </ModalFooter>
          </form>
        )}
      </ModalContent>
    </Modal>
  );
};

export default UpdateSalaryModal;
